import { useToggle } from "../../../../utils/Hooks"
import { FleContinueWrapper, FleLayout } from "../FleLayout"
import { BackArrow, ContinueArrow, Skip, SwitchH2ToBodyLarge } from "../Molecules"
import { InfoCircle } from "../../atoms/Links"
import { Spacer } from "../../atoms/Atoms"
import GenderIdentityChoices from "../../../../content/GenderIdentityChoices"
import FLEInformationModal from "components/views/modal/FLEInformationModal"
import React, { useEffect, useLayoutEffect, useState } from "react"
import L10n from "../../../../lib/carrot-l10n"
import { TopSection } from "../TopSection"
import { useSelector } from "react-redux"
import { getBenefitConfiguration } from "reducers/benefitConfiguration"
import { useHistory } from "react-router"
import useAboutYou from "../hooks/useAboutYou"
import { useAboutYouFlowControls } from "../context/AboutYouFlowContext"
import { AboutYouTopSection } from "components/carrot-plans/shared/ProgressTracker"
import { SelectButton } from "../../atoms/buttons/SelectButton"
import { ClickExpandStyledInput, ClickExpandStyledInputBox } from "../../atoms/forms/StyledInputs"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

export const GenderIdentity = ({ next, previous }) => {
  const history = useHistory()
  const { goBack, wentBack } = useAboutYouFlowControls()
  const { data, updateAboutYou, hasQuestionBeenSkipped, continueLoading } = useAboutYou()
  const benefitConfiguration = useSelector(getBenefitConfiguration)
  const [genderIdentityValue, setGenderIdentityValue] = useState(data?.genderIdentity)
  const [genderIdentityOtherValue, setGenderIdentityOtherValue] = useState(data?.genderIdentityOther || "")
  const [showMemberGenderIdentityModal, toggleMemberGenderIdentityModal] = useToggle(false)
  const [showAnotherGenderIdentityInput, setShowAnotherGenderIdentityInput] = useState(
    genderIdentityValue === "ANOTHER_GENDER_IDENTITY"
  )

  function onContinue() {
    history.push(next)
  }

  useLayoutEffect(() => {
    if (!benefitConfiguration.featureConfig.isLgtbqEnabled) {
      history.replace(next)
    }
    if (!wentBack && (data?.genderIdentity || hasQuestionBeenSkipped("genderIdentity"))) {
      history.push(next)
    }
  }, [data?.genderIdentity, history, wentBack, benefitConfiguration.featureConfig.isLgtbqEnabled, next])

  useEffect(() => {
    setGenderIdentityValue(data?.genderIdentity)
    setGenderIdentityOtherValue(data?.genderIdentityOther)
    setShowAnotherGenderIdentityInput(data?.genderIdentity === "ANOTHER_GENDER_IDENTITY")
  }, [data?.genderIdentity, data?.genderIdentityOther])

  if (!benefitConfiguration.featureConfig.isLgtbqEnabled) {
    onContinue(false)
    return null
  }

  async function onClickGenderIdentity(id) {
    setGenderIdentityValue(id)
    setGenderIdentityOtherValue(null)
    setShowAnotherGenderIdentityInput(false)
    await updateAboutYou({ genderIdentity: id, genderIdentityOther: "nullOut" })
    onContinue()
  }

  async function onClickAnotherGenderIdentity(id) {
    setGenderIdentityValue(id)
    setShowAnotherGenderIdentityInput(true)
    await updateAboutYou({ genderIdentity: id })
  }

  async function onClickInputBox() {
    setGenderIdentityOtherValue(null)
    await updateAboutYou({ genderIdentityOther: "nullOut" })
  }

  async function onClickContinue() {
    if (genderIdentityValue) {
      await updateAboutYou({ genderIdentityOther: genderIdentityOtherValue })
      onContinue()
    } else {
      await onSkip()
    }
  }

  async function onSkip() {
    await updateAboutYou({ genderIdentity: "skip", genderIdentityOther: "nullOut" })
    onContinue()
  }

  // This is an anti pattern that we should fix eventually. Components should not be defined
  // inside other components.
  const InputBox = () => (
    <ClickExpandStyledInputBox onClick={onClickInputBox}>
      <p style={{ padding: "0px 24px" }}>Another gender identity</p>
      <Spacer height={0.5} />
      <ClickExpandStyledInput
        id="genderIdentityOther"
        placeholder="Optional details"
        value={genderIdentityOtherValue || ""}
        onChange={(e) => {
          setGenderIdentityOtherValue(e.target.value)
        }}
        onClick={(e) => e.stopPropagation()}
        autoFocus
        maxLength="200"
      />
      <div className="flex flex-row-reverse justify-between items-center" style={{ margin: "8px 24px 0px 24px" }}>
        {genderIdentityOtherValue && genderIdentityOtherValue.length >= 200 * 0.75 && (
          <p className="black-40">{genderIdentityOtherValue.length}/200</p>
        )}
      </div>
    </ClickExpandStyledInputBox>
  )

  return (
    <FleLayout
      closeButton
      bottomContent={
        <FleContinueWrapper>
          <Skip onClick={onSkip} onClickOnly enabled={!continueLoading} />
          <ContinueArrow enabled={!continueLoading} onClick={onClickContinue} />
        </FleContinueWrapper>
      }
    >
      <AboutYouTopSection previous={previous}>
        <TopSection progressStep={2} leftOrBackArrow={<BackArrow onClick={() => goBack(previous)} />} />
      </AboutYouTopSection>
      <div className="flex flex-row items-center">
        <SwitchH2ToBodyLarge>{l`What's your gender identity?`}</SwitchH2ToBodyLarge>
        <InfoCircle onClick={toggleMemberGenderIdentityModal} />
      </div>
      <Spacer height={3} />
      {GenderIdentityChoices.map(({ id, label }) =>
        id === "ANOTHER_GENDER_IDENTITY" ? (
          showAnotherGenderIdentityInput ? (
            <InputBox key="another-gender-identity-input" />
          ) : (
            <div key={id} className="flex flex-row justify-end w-100 mb1">
              <SelectButton
                outline={id === genderIdentityValue}
                onClick={() => onClickAnotherGenderIdentity(id)}
                type="button"
                disabled={continueLoading}
              >
                {label}
              </SelectButton>
            </div>
          )
        ) : (
          <div key={id} className="flex flex-row justify-end w-100 mb1">
            <SelectButton
              outline={id === genderIdentityValue}
              onClick={() => onClickGenderIdentity(id)}
              disabled={continueLoading}
            >
              {label}
            </SelectButton>
          </div>
        )
      )}
      {showMemberGenderIdentityModal ? (
        <FLEInformationModal
          benefitConfiguration={benefitConfiguration}
          onClose={toggleMemberGenderIdentityModal}
          header={l`fle2.aboutyou.genderidentity`}
          source={l`fle2.memberGenderIdentityFull`}
        />
      ) : null}
    </FleLayout>
  )
}
